/**
 * Тип рекламной кампании — словами, а не кодом.
 *
 * ЗАЧЕМ ЭТО В СПИСКЕ. Две кампании с одинаковым ДРР ведут себя
 * по-разному, если у одной ставка общая на поиск и рекомендации,
 * а у другой раздельная: во второй можно отключить площадку, в первой
 * нечего отключать. Не видя типа, человек ищет в настройках то, чего
 * там нет.
 *
 * ЧЕТЫРЕ РОДА КАМПАНИЙ, и различие между ними — в том, чем управляют:
 *
 *   Автоматическая — отдельный тип Wildberries (код 8);
 *   Единая         — ставка ОДНА на поиск и полки, развести их нельзя;
 *   Поиск          — ставки РАЗДЕЛЬНЫЕ, и площадки включаются порознь:
 *                    только поиск, только рекомендации или обе;
 *   CPC            — то же, что «Поиск», но платят за КЛИК, а не
 *                    за тысячу показов.
 *
 * ═══════════════════════════════════════════════════════════════════
 * «ЕДИНАЯ» — ЭТО `bid_type`, А НЕ ТИП КАМПАНИИ.
 *
 * Кабинет Wildberries подписывает такую кампанию словом «Единая»,
 * а мы писали «Поиск · поиск + рекомендации» — то есть обещали две
 * ручки там, где ручка одна. Владелец поймал это 18.08.2026 и прислал
 * снимок своего кабинета WB рядом с нашим.
 *
 * Снято с базы, а не выведено из названий (18.08.2026):
 *
 *   advert_id | type    | bid_type | payment_type
 *   38042230  | auction | unified  | cpm     ← в кабинете WB «Единая»
 *   38723641  | auction | manual   | cpc
 *
 * Обе кампании — `auction`. Различает их именно `bid_type`, и потому
 * род читается ОТТУДА, а не из типа. Тип отвечает на другой вопрос:
 * где кампания вообще может показываться.
 * ═══════════════════════════════════════════════════════════════════
 *
 * ═══════════════════════════════════════════════════════════════════
 * ПОЧЕМУ АВТОМАТИЧЕСКИЕ КАМПАНИИ ПОКАЗЫВАЛИСЬ КАК «ПОИСК»
 *
 * Здесь стояло `Number(c.type) === 8`, и рядом — примечание «8 —
 * автоматическая, 9 — аукцион». Примечание описывало ответ Wildberries,
 * а поле `type` в нашей базе хранит НЕ КОД, А СЛОВО: выгрузка переводит
 * код в имя (`8 → 'auto'`, `9 → 'auction'`) ещё при записи. `Number('auto')`
 * — это `NaN`, и `NaN === 8` ложно ВСЕГДА. Значит ветка «автоматическая»
 * не срабатывала ни разу за всё время, и каждая кампания кабинета
 * подписывалась «Поиск» либо «CPC».
 *
 * ТА ЖЕ СЕМЬЯ ОШИБОК, ЧТО И ВСЕ ОСТАЛЬНЫЕ В АВГУСТЕ: мы поверили своей
 * копии — примечанию о том, что в поле лежит код, — больше, чем источнику,
 * то есть коду выгрузки, который это поле заполняет. Проверка ниже
 * (`campaign-type.spec.ts`) теперь ЧИТАЕТ таблицу перевода из выгрузки
 * и требует, чтобы каждое её значение здесь было названо. Разойтись
 * молча они больше не могут.
 * ═══════════════════════════════════════════════════════════════════
 *
 * ПЛОЩАДКИ ВЫВЕДЕНЫ ИЗ СТАВОК, А НЕ ПОЛУЧЕНЫ ОТ WILDBERRIES. В нашей
 * выгрузке состава кампании нет поля «где показывать» — есть две ставки
 * на товар. Ненулевая ставка поиска означает, что поиск включён; то же
 * с рекомендациями. Это вывод из данных, а не сообщение WB, и потому
 * он ошибётся ровно в одном случае: площадка включена, а ставка
 * на ней нулевая — то есть показов по ней всё равно нет.
 */

export interface TypeInput {
  /**
   * Тип кампании в НАШЕЙ базе — слово, а не код Wildberries.
   *
   * Перевод кода в слово делает выгрузка (`TYPE_BY_CODE` в
   * `src/sync/campaign-sync.service.ts`); числа сюда не доходят.
   */
  type: string | number | null;
  /** Чем платим: cpm — за тысячу показов, cpc — за клик. */
  paymentType: string | null;
  /** Ставка ручная или единая. */
  bidType: string | null;
  /** Есть ли хоть у одного товара ненулевая ставка поиска. */
  hasSearchBid?: boolean;
  hasRecomBid?: boolean;
}

/** Автоматическая кампания: код Wildberries и наше имя для него. */
export const AUTO_TYPE = 8;
export const AUTO = 'auto';

/**
 * Как называется каждый тип из выгрузки.
 *
 * ЗДЕСЬ ПЕРЕЧИСЛЕНЫ ВСЕ, А НЕ ТОЛЬКО ХОДОВЫЕ. Ключи совпадают
 * с `TYPE_BY_CODE` выгрузки — это требование проверки, а не пожелание:
 * стоит там появиться новому типу, и набор станет красным, вместо того
 * чтобы кампания молча подписалась чужим родом.
 *
 * `null` означает «род определяется оплатой» — Поиск либо CPC.
 * Так устроены только аукцион и поиск: в остальных выбора нет.
 */
const KIND_BY_TYPE: Readonly<Record<string, string | null>> = {
  auto: 'Автоматическая',
  // Аукцион — нынешний вид раздельных кампаний. Владелец называет его
  // «Поиск», и здесь мы говорим на его языке, а не на языке WB.
  auction: null,
  search: null,
  // Старые виды, заводить которые Wildberries больше не даёт. Живые
  // кампании таких видов в кабинетах ещё встречаются.
  catalog: 'Каталог',
  card: 'Карточка товара',
  search_catalog: 'Поиск и каталог',
};

export interface CampaignType {
  /** Одно слово: «Автоматическая», «Поиск», «CPC». */
  kind: string;
  /** Где показывается — плашками. Пусто, если ставок нет вовсе. */
  places: string[];
  /** Подробности, которые нужны редко: по наведению. */
  hint: string;
}

/**
 * Тип кампании — коротко для экрана, подробно для наведения.
 *
 * ДЛИННАЯ СТРОКА ПОД КАЖДОЙ КАМПАНИЕЙ ЧИТАЕТСЯ РОВНО ОДИН РАЗ.
 * «Поиск · оплата за 1000 показов · ручная ставка · поиск +
 * рекомендации» — четыре факта, из которых глазу нужен один: род.
 * Тридцать шесть таких строк подряд превращаются в серый шум,
 * и в нём теряется как раз то, ради чего подпись заводилась.
 *
 * Поэтому на экране остаётся слово и площадки плашками, а способ
 * оплаты и вид ставки уходят в подсказку: они не меняются годами
 * и спрашиваются раз в месяц.
 */
export function campaignType(c: TypeInput): CampaignType {
  const auto = isAuto(c.type);
  const one = isUnified(c.bidType);
  const cpc = (c.paymentType ?? '').toLowerCase() === 'cpc';

  const kind = auto
    ? 'Автоматическая'
    : one
      ? UNIFIED_KIND
      : (named(c.type) ?? (cpc ? 'CPC' : 'Поиск'));

  const hint = [
    auto || one
      ? `Ставка одна на поиск и ${SHELF} сразу — развести их Wildberries не даёт`
      : cpc
        ? 'Оплата за клик, ставки для площадок задаются порознь'
        : 'Оплата за 1000 показов, ставки для площадок задаются порознь',
    auto || one ? 'единая ставка' : 'ручная ставка',
    cpc ? 'оплата за клик' : 'оплата за 1000 показов',
    // У кампании с единой ставкой площадки из ставок не выводятся:
    // ставка одна и стоит в обоих полях выгрузки независимо от того,
    // где кампания на самом деле показывается
    auto || one
      ? 'Площадки у неё не разделяются'
      : 'Площадки выведены из ставок: ненулевая ставка означает, что площадка работает',
  ].join('. ');

  return { kind, places: placements(c), hint };
}

/** Имя рода для кампании с единой ставкой — слово самого Wildberries. */
export const UNIFIED_KIND = 'Единая';

/**
 * Как зовётся вторая площадка.
 *
 * КАБИНЕТ WILDBERRIES ЗОВЁТ ЕЁ «ПОЛКИ», ЭТАЛОН — «РЕКОМЕНДАЦИИ»,
 * И ВЫБРАНО ВТОРОЕ. Слово вынесено в одно место нарочно, и 28.08
 * это окупилось правкой в одну строку.
 *
 * ПОЧЕМУ ПОМЕНЯЛИ. Довод за «полки» был такой: владелец каждый день
 * смотрит в кабинет WB, и наше слово, отличающееся от тамошнего,
 * заставляет его переводить. Довод верный — но он не заметил, что
 * переводить приходилось ВНУТРИ ОДНОГО ЭКРАНА: у обычной кампании
 * площадки выводятся из ставок и зовутся «поиск» и «рекомендации»,
 * а у единой рядом стояло «поиск и полки». Два слова об одном месте
 * в соседних строках одной таблицы.
 *
 * Владелец на это и указал: «также как и в других помечать поиск
 * и рекомендации, только единой плашкой как это и сделано сейчас».
 * Расхождение с чужим кабинетом он переводит один раз; расхождение
 * внутри нашего — каждый раз, когда сравнивает две строки.
 */
export const SHELF = 'рекомендации';

/**
 * Одна ли ставка у кампании.
 *
 * ЭТО РЕШАЕТ НЕ ТОЛЬКО ПОДПИСЬ. От ответа зависит, рисовать одно поле
 * ставки или два: у единой кампании второе поле — рычаг, который никуда
 * не подключён.
 */
export function isUnified(bidType: string | null | undefined): boolean {
  return (bidType ?? '').trim().toLowerCase() === 'unified';
}

/**
 * Автоматическая ли.
 *
 * ПРИНИМАЕТ И СЛОВО, И КОД. В базе лежит слово, но тот же разбор зовут
 * с ответом Wildberries на руках, где лежит число, — и различать это
 * по месту вызова значит однажды перепутать. Оба ответа на один вопрос
 * дают одно и то же.
 */
export function isAuto(type: string | number | null): boolean {
  if (type === null) return false;
  if (typeof type === 'number') return type === AUTO_TYPE;
  const t = type.trim().toLowerCase();
  return t === AUTO || t === String(AUTO_TYPE);
}

/** Имя рода, если оно не зависит от оплаты. */
function named(type: string | number | null): string | null {
  if (type === null) return null;
  const t = String(type).trim().toLowerCase();
  return KIND_BY_TYPE[t] ?? null;
}

/** Совместимость: одна строка целиком — там, где плашки не к месту. */
export function typeLabel(c: TypeInput): string {
  const t = campaignType(c);
  return t.places.length ? `${t.kind} (${t.places.join(' + ')})` : t.kind;
}

function placements(c: TypeInput): string[] {
  /**
   * У ЕДИНОЙ КАМПАНИИ ПЛОЩАДКИ НЕ РАЗДЕЛЯЮТСЯ, и выводить их из ставок
   * тем более нельзя: ставка одна и стоит в обоих полях выгрузки.
   * «поиск + рекомендации» двумя плашками читалось бы как две настройки
   * — ровно то заблуждение, из-за которого мы и рисовали два поля.
   */
  if (isUnified(c.bidType)) return [`поиск и ${SHELF}`];

  /**
   * У АВТОМАТИЧЕСКОЙ ПЛОЩАДКИ НЕ ПЕРЕЧИСЛЯЕМ. Ставка у неё тоже одна,
   * но показывается она не только в поиске и на полках: Wildberries
   * сам решает, где её ставить, и список мест мы не знаем. Написать
   * «поиск и полки» значит сузить — то есть соврать в другую сторону.
   * Ниже остаётся вывод из ставок: он говорит только о том,
   * что подтверждено ненулевой ставкой.
   */


  const out: string[] = [];
  if (c.hasSearchBid === true) out.push('поиск');
  // ТО ЖЕ СЛОВО, ЧТО У ЕДИНОЙ: здесь стоял литерал, и пока `SHELF`
  // был «полками», экран называл одно место двумя именами.
  if (c.hasRecomBid === true) out.push(SHELF);
  // Ни одной ненулевой ставки — площадку назвать нечем, и выдумывать
  // её нельзя: «поиск + рекомендации» у кампании без ставок означало бы
  // обещание показов, которых нет.
  return out;
}

/** Имена типов, которые здесь названы, — для проверки против выгрузки. */
export const KNOWN_TYPES: readonly string[] = Object.keys(KIND_BY_TYPE);
